// IANA timezones.
//
// CURATED_TIMEZONE_OPTIONS is a hand-written shortlist with friendly labels. It is a
// DISPLAY convenience only — it must never be used to decide whether a stored value is
// valid. It covers ~91 of the ~418 zones the runtime knows about, and the backend's own
// address backfill writes zones that are not on it (America/Detroit, America/Vancouver,
// America/Iqaluit, ...). Treating it as a validator silently replaced those with the
// admin's browser zone and wrote the wrong value back on save.
//
// Use ensureTimezoneOption() / getTimezoneOptionsFor() instead of matching against a list.
export const CURATED_TIMEZONE_OPTIONS = [
  {
    id: "Etc/GMT+12",
    label: "(UTC-12:00) International Date Line West (IDLW)",
  },
  { id: "Pacific/Midway", label: "(UTC-11:00) Midway Island, Samoa (SST)" },
  { id: "Pacific/Honolulu", label: "(UTC-10:00) Hawaii (HST)" },
  { id: "America/Anchorage", label: "(UTC-09:00) Alaska (AKST/AKDT)" },
  {
    id: "America/Los_Angeles",
    label: "(UTC-08:00) Pacific Time (US & Canada) (PST/PDT)",
  },
  {
    id: "America/Tijuana",
    label: "(UTC-08:00) Tijuana, Baja California (PST/PDT)",
  },
  {
    id: "America/Denver",
    label: "(UTC-07:00) Mountain Time (US & Canada) (MST/MDT)",
  },
  {
    id: "America/Chihuahua",
    label: "(UTC-07:00) Chihuahua, La Paz, Mazatlan (MST/MDT)",
  },
  { id: "America/Phoenix", label: "(UTC-07:00) Arizona (MST)" },
  {
    id: "America/Chicago",
    label: "(UTC-06:00) Central Time (US & Canada) (CST/CDT)",
  },
  { id: "America/Regina", label: "(UTC-06:00) Saskatchewan (CST)" },
  {
    id: "America/Mexico_City",
    label: "(UTC-06:00) Guadalajara, Mexico City, Monterrey (CST/CDT)",
  },
  { id: "America/Guatemala", label: "(UTC-06:00) Central America (CST)" },
  {
    id: "America/New_York",
    label: "(UTC-05:00) Eastern Time (US & Canada) (EST/EDT)",
  },
  {
    id: "America/Toronto",
    label: "(UTC-05:00) Toronto (EST/EDT)",
  },
  {
    id: "America/Indiana/Indianapolis",
    label: "(UTC-05:00) Indiana (East) (EST/EDT)",
  },
  {
    id: "America/Bogota",
    label: "(UTC-05:00) Bogota, Lima, Quito (COT/PET/ECT)",
  },
  {
    id: "America/Halifax",
    label: "(UTC-04:00) Atlantic Time (Canada) (AST/ADT)",
  },
  { id: "America/Caracas", label: "(UTC-04:00) Caracas (VET)" },
  { id: "America/La_Paz", label: "(UTC-04:00) La Paz (BOT)" },
  { id: "America/Santiago", label: "(UTC-04:00) Santiago (CLT/CLST)" },
  { id: "America/St_Johns", label: "(UTC-03:30) Newfoundland (NST/NDT)" },
  { id: "America/Sao_Paulo", label: "(UTC-03:00) Brasilia (BRT/BRST)" },
  {
    id: "America/Argentina/Buenos_Aires",
    label: "(UTC-03:00) Buenos Aires (ART)",
  },
  { id: "America/Nuuk", label: "(UTC-03:00) Greenland (WGT/WGST)" },
  { id: "America/Montevideo", label: "(UTC-03:00) Montevideo (UYT)" },
  { id: "America/Noronha", label: "(UTC-02:00) Mid-Atlantic (FNT)" },
  { id: "Atlantic/Azores", label: "(UTC-01:00) Azores (AZOT/AZOST)" },
  { id: "Atlantic/Cape_Verde", label: "(UTC-01:00) Cape Verde Is. (CVT)" },
  { id: "UTC", label: "(UTC) Coordinated Universal Time (UTC)" },
  {
    id: "Europe/London",
    label: "(UTC) Dublin, Edinburgh, Lisbon, London (GMT/BST)",
  },
  { id: "Atlantic/Reykjavik", label: "(UTC) Monrovia, Reykjavik (GMT)" },
  {
    id: "Europe/Berlin",
    label:
      "(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna (CET/CEST)",
  },
  {
    id: "Europe/Paris",
    label: "(UTC+01:00) Brussels, Copenhagen, Madrid, Paris (CET/CEST)",
  },
  {
    id: "Europe/Belgrade",
    label:
      "(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague (CET/CEST)",
  },
  {
    id: "Europe/Warsaw",
    label: "(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb (CET/CEST)",
  },
  { id: "Africa/Casablanca", label: "(UTC+01:00) Casablanca (WET/WEST)" },
  { id: "Africa/Lagos", label: "(UTC+01:00) West Central Africa (WAT)" },
  { id: "Europe/Athens", label: "(UTC+02:00) Athens, Bucharest (EET/EEST)" },
  { id: "Europe/Istanbul", label: "(UTC+02:00) Istanbul (TRT)" },
  {
    id: "Europe/Helsinki",
    label:
      "(UTC+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius (EET/EEST)",
  },
  { id: "Africa/Cairo", label: "(UTC+02:00) Cairo (EET)" },
  { id: "Africa/Johannesburg", label: "(UTC+02:00) Harare, Pretoria (SAST)" },
  { id: "Asia/Jerusalem", label: "(UTC+02:00) Jerusalem (IST/IDT)" },
  { id: "Europe/Minsk", label: "(UTC+03:00) Minsk (GMT+3)" },
  { id: "Asia/Baghdad", label: "(UTC+03:00) Baghdad (AST)" },
  { id: "Asia/Riyadh", label: "(UTC+03:00) Kuwait, Riyadh (AST)" },
  { id: "Africa/Nairobi", label: "(UTC+03:00) Nairobi (EAT)" },
  {
    id: "Europe/Moscow",
    label: "(UTC+03:00) Moscow, St. Petersburg, Volgograd (MSK)",
  },
  { id: "Asia/Tehran", label: "(UTC+03:30) Tehran (IRST/IRDT)" },
  { id: "Asia/Dubai", label: "(UTC+04:00) Abu Dhabi, Muscat (GST)" },
  { id: "Asia/Baku", label: "(UTC+04:00) Baku (AZT/AZST)" },
  { id: "Asia/Yerevan", label: "(UTC+04:00) Yerevan (AMT)" },
  { id: "Asia/Tbilisi", label: "(UTC+04:00) Tbilisi (GET)" },
  { id: "Indian/Mauritius", label: "(UTC+04:00) Port Louis (MUT)" },
  { id: "Asia/Kabul", label: "(UTC+04:30) Kabul (AFT)" },
  { id: "Asia/Karachi", label: "(UTC+05:00) Islamabad, Karachi (PKT)" },
  { id: "Asia/Tashkent", label: "(UTC+05:00) Tashkent (UZT)" },
  { id: "Asia/Yekaterinburg", label: "(UTC+05:00) Ekaterinburg (YEKT)" },
  {
    id: "Asia/Kolkata",
    label: "(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi (IST)",
  },
  { id: "Asia/Colombo", label: "(UTC+05:30) Sri Jayawardenepura (IST)" },
  { id: "Asia/Kathmandu", label: "(UTC+05:45) Kathmandu (NPT)" },
  { id: "Asia/Dhaka", label: "(UTC+06:00) Astana, Dhaka (BST)" },
  { id: "Asia/Almaty", label: "(UTC+06:00) Almaty (ALMT)" },
  { id: "Asia/Omsk", label: "(UTC+06:00) Omsk (OMST)" },
  { id: "Asia/Yangon", label: "(UTC+06:30) Yangon (Rangoon) (MMT)" },
  {
    id: "Asia/Bangkok",
    label: "(UTC+07:00) Bangkok, Hanoi, Jakarta (ICT/WIB)",
  },
  { id: "Asia/Krasnoyarsk", label: "(UTC+07:00) Krasnoyarsk (KRAT)" },
  {
    id: "Asia/Shanghai",
    label: "(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi (CST)",
  },
  {
    id: "Asia/Singapore",
    label: "(UTC+08:00) Kuala Lumpur, Singapore (SGT/MYT)",
  },
  { id: "Asia/Taipei", label: "(UTC+08:00) Taipei (CST)" },
  { id: "Australia/Perth", label: "(UTC+08:00) Perth (AWST)" },
  { id: "Asia/Irkutsk", label: "(UTC+08:00) Irkutsk (IRKT)" },
  { id: "Asia/Ulaanbaatar", label: "(UTC+08:00) Ulaanbaatar (ULAT)" },
  { id: "Asia/Tokyo", label: "(UTC+09:00) Osaka, Sapporo, Tokyo (JST)" },
  { id: "Asia/Seoul", label: "(UTC+09:00) Seoul (KST)" },
  { id: "Asia/Yakutsk", label: "(UTC+09:00) Yakutsk (YAKT)" },
  { id: "Australia/Adelaide", label: "(UTC+09:30) Adelaide (ACST/ACDT)" },
  { id: "Australia/Darwin", label: "(UTC+09:30) Darwin (ACST)" },
  { id: "Australia/Brisbane", label: "(UTC+10:00) Brisbane (AEST)" },
  {
    id: "Australia/Sydney",
    label: "(UTC+10:00) Canberra, Melbourne, Sydney (AEST/AEDT)",
  },
  { id: "Australia/Hobart", label: "(UTC+10:00) Hobart (AEST/AEDT)" },
  { id: "Pacific/Guam", label: "(UTC+10:00) Guam, Port Moresby (ChST/PGT)" },
  { id: "Asia/Vladivostok", label: "(UTC+10:00) Vladivostok (VLAT)" },
  { id: "Asia/Magadan", label: "(UTC+11:00) Magadan (MAGT)" },
  {
    id: "Pacific/Guadalcanal",
    label: "(UTC+11:00) Solomon Is., New Caledonia (SBT/NCT)",
  },
  {
    id: "Pacific/Auckland",
    label: "(UTC+12:00) Auckland, Wellington (NZST/NZDT)",
  },
  { id: "Pacific/Fiji", label: "(UTC+12:00) Fiji (FJT)" },
  {
    id: "Asia/Kamchatka",
    label: "(UTC+12:00) Petropavlovsk-Kamchatsky (PETT)",
  },
  { id: "Pacific/Tongatapu", label: "(UTC+13:00) Nuku'alofa (TOT)" },
  { id: "Pacific/Apia", label: "(UTC+13:00) Samoa (WST)" },
];

export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return "UTC";
  }
};

/**
 * True when the runtime recognises the id as a real zone.
 *
 * This is the ONLY validity test worth applying. Do not test list membership: the
 * enumeration below is runtime-dependent and different ICU builds disagree even on
 * canonical spellings (some report Asia/Calcutta for Asia/Kolkata, America/Godthab for
 * America/Nuuk, America/Indianapolis for America/Indiana/Indianapolis). Both spellings
 * are accepted by DateTimeFormat, so validating this way is stable across browsers.
 */
export const isValidTimezone = (timezoneId) => {
  if (!timezoneId || typeof timezoneId !== "string") return false;

  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezoneId });
    return true;
  } catch (error) {
    return false;
  }
};

// Full set the runtime knows about. Empty on very old browsers, which is fine — the
// curated list plus the stored value still give a usable picker.
const getRuntimeTimezoneIds = () => {
  try {
    return typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : [];
  } catch (error) {
    return [];
  }
};

// "(UTC-06:00)" for the zone as of now. Computed rather than hardcoded so it follows
// DST instead of going stale — note the curated labels above still embed a fixed offset
// and will read wrong for half the year.
export const getOffsetLabel = (timezoneId) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneId,
      timeZoneName: "longOffset",
    }).formatToParts(new Date());

    const name = parts.find((part) => part.type === "timeZoneName")?.value;
    if (!name) return "";

    // longOffset yields "GMT-06:00", or bare "GMT" at exactly UTC.
    return name === "GMT" ? "(UTC+00:00)" : `(${name.replace("GMT", "UTC")})`;
  } catch (error) {
    return "";
  }
};

// Turn a raw id into a picker option: "(UTC-05:00) America/Detroit".
const buildTimezoneOption = (timezoneId) => {
  const offset = getOffsetLabel(timezoneId);
  const readable = timezoneId.replace(/_/g, " ");

  return {
    id: timezoneId,
    label: offset ? `${offset} ${readable}` : readable,
  };
};

/**
 * Curated entries first (friendlier labels), then every other zone the runtime knows,
 * alphabetically. Built once at module load.
 */
export const TIMEZONE_OPTIONS = (() => {
  const curatedIds = new Set(CURATED_TIMEZONE_OPTIONS.map(({ id }) => id));

  const generated = getRuntimeTimezoneIds()
    .filter((id) => !curatedIds.has(id))
    .sort((a, b) => a.localeCompare(b))
    .map(buildTimezoneOption);

  return [...CURATED_TIMEZONE_OPTIONS, ...generated];
})();

export const findTimezoneOption = (timezoneId) => {
  if (!timezoneId) return null;
  return TIMEZONE_OPTIONS.find((option) => option.id === timezoneId) || null;
};

/**
 * Always yields an option for any non-empty id, synthesising one when the id isn't in
 * TIMEZONE_OPTIONS. Returns null only for an empty id.
 *
 * This is what keeps a stored zone intact: the form round-trips the exact string it was
 * given rather than snapping to the nearest known entry. Normalising aliases is the
 * backend's job — guessing here is how the wrong zone gets written back.
 */
export const ensureTimezoneOption = (timezoneId) => {
  if (!timezoneId) return null;
  return findTimezoneOption(timezoneId) || buildTimezoneOption(timezoneId);
};

/**
 * Options for a picker that currently holds `timezoneId`, guaranteeing the selected
 * value is always present so the control can render it.
 */
export const getTimezoneOptionsFor = (timezoneId) => {
  if (!timezoneId || findTimezoneOption(timezoneId)) return TIMEZONE_OPTIONS;
  return [buildTimezoneOption(timezoneId), ...TIMEZONE_OPTIONS];
};

/**
 * Turn a `GET /restapi/timezones` entry into a picker option.
 *
 * The API deliberately ships labels with no UTC offset (an embedded offset is wrong for
 * half the year under DST and goes stale when tzdb changes), so the offset is added here
 * at render time. Entries without a usable label fall back to the generated form.
 */
export const decorateTimezoneOption = (entry) => {
  const id = entry?.id;
  if (!id) return null;
  if (!entry?.label) return buildTimezoneOption(id);

  const offset = getOffsetLabel(id);

  return {
    ...entry,
    label: offset ? `${offset} ${entry.label}` : entry.label,
  };
};

/**
 * Ensure `timezoneId` is selectable in `options`, appending a synthesised entry when the
 * list doesn't contain it. This is what stops a narrowed or stale list from dropping a
 * stored value — the same failure the curated-list-as-validator bug caused.
 */
export const withTimezoneSelectable = (options, timezoneId) => {
  const list = Array.isArray(options) ? options : [];
  if (!timezoneId || list.some((option) => option?.id === timezoneId)) return list;

  return [ensureTimezoneOption(timezoneId), ...list];
};

